import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart } from '@fortawesome/pro-light-svg-icons';

import { cn } from '@v2/shared/lib/utils';
import { CartCount } from '@v2/shared/components/CartCount';

type Props = {
  /** The count for the badge, or null while the cart has not loaded — see Full's CartLink. */
  cartCount: number | null;
  onClick: () => void;
  isMobile?: boolean;
};

/**
 * The header's cart control on a sale page.
 *
 * Full's is a link to `/cart`. This one is a button, because on this page the cart is a
 * drawer and clicking it must not leave the sale — a customer three screens into a
 * hundred-product grid should not lose their place to look at their basket. That is the
 * whole difference, so everything else is deliberately Full's: the same icon, the same
 * hit area, the same badge component.
 *
 * A button rather than a link with a click handler, and not only for semantics: a link
 * offers "open in new tab", and the tab it opens would be a page with no drawer on it.
 */
export default function CartButton({ cartCount, onClick, isMobile = false }: Props) {
  const label = cartCount === null ? 'Cart' : `Cart, ${cartCount} ${cartCount === 1 ? 'item' : 'items'}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('text-2xl', 'text-slate-800 hover:text-slate-900 active:text-slate-950', 'group cursor-pointer relative', {
        'inline-flex p-2': isMobile,
        flex: !isMobile,
      })}
      aria-label={label}
      aria-haspopup="dialog"
    >
      <div
        className={cn('flex items-center justify-center rounded-md', 'bg-white group-hover:bg-slate-100 group-active:bg-slate-200', {
          'w-9 h-9': isMobile,
          'h-12 px-3': !isMobile,
        })}
      >
        <FontAwesomeIcon icon={faShoppingCart} />
      </div>
      {cartCount !== null && <CartCount className="absolute top-1 right-1" count={cartCount} />}
    </button>
  );
}
